import type { TFunction } from 'i18next'

import z from 'zod'

import { filterSchema } from '@/shared/components/filter/validation'

export const getFormSchema = (t: TFunction) =>
	z.object({
		name: z.string().min(1, 'Please enter a name'),
		optimization_group_ids: z
			.array(z.string())
			.min(
				1,
				t(
					'anchoring_management.strategy.validation.optimization_group_required'
				)
			),
		settings: z.object({
			groups: z
				.array(
					z.object({
						name: z.string(),
						id: z.string().optional(),
						filters: z
							.array(filterSchema)
							.min(1, 'Please add at least one filter'),
						aggregation: z.array(z.string()),
						clusterization: z
							.array(
								z.object({
									column: z.string().min(1),
									percent: z.number().gt(0).max(100).nullable(),
								})
							)
							.superRefine((conditions, ctx) => {
								const usedColumns = new Set<string>()

								conditions.forEach(({ column, percent }, index) => {
									if (percent === null) {
										ctx.addIssue({
											code: z.ZodIssueCode.custom,
											message: t(
												'anchoring_management.strategy.tab.anchor_families.clusterization.percent_required',
												{ defaultValue: 'Tolerance % is required' }
											),
											path: [index, 'percent'],
										})
									}

									if (usedColumns.has(column)) {
										ctx.addIssue({
											code: z.ZodIssueCode.custom,
											message: t(
												'anchoring_management.strategy.tab.anchor_families.clusterization.duplicated_column',
												{ defaultValue: 'This attribute is already used' }
											),
											path: [index, 'column'],
										})
									}

									usedColumns.add(column)
								})
							}),
					})
				)
				.min(1, 'Please add at least one family'),
			indexes: z.object({
				index_type: z.enum(['add', 'mul']),
				columns: z
					.array(
						z
							.string()
							.nonempty(
								t(
									'anchoring_management.strategy.validation.no_empty_price_column',
									{ defaultValue: 'Please select at least one price column' }
								)
							)
					)
					.min(
						1,
						t(
							'anchoring_management.strategy.validation.no_empty_price_column',
							{ defaultValue: 'Please select at least one price column' }
						)
					),
			}),
			anchor_selection: z.object({
				filters: z.array(filterSchema),
				sorting: z
					.array(
						z.object({ name: z.string(), direction: z.enum(['asc', 'desc']) })
					)
					.min(
						1,
						t(
							'anchoring_management.strategy.tab.anchor_selection.sorting_required',
							{ defaultValue: 'At least one sort column is required' }
						)
					),
			}),
			general: z.object({
				propagate_generated_anchors: z.boolean(),
				cleanup_generated_anchors: z.boolean(),
			}),
		}),
	})
